import React, { useState, useEffect, useRef, forwardRef } from 'react'
import cls from './Input.module.scss'

interface InputProps {
  value: string
  name: string
  title?: string
  type?: 'text' | 'password' | 'email' | 'number'
  placeholder?: string
  onChangeHandler: (name: string, value: string) => void
  onBlurHandler?: (name: string, value: string) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>, name?: string) => void
  disabled?: boolean
  error?: boolean
  errorMessage?: string
  focus?: boolean
  className?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  autoComplete?: string
  required?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      name,
      title,
      type = 'text',
      placeholder,
      onChangeHandler,
      onBlurHandler,
      onFocus,
      disabled = false,
      error = false,
      errorMessage,
      focus = false,
      className = '',
      onKeyDown,
      autoComplete = 'off',
      required = false,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(value)
    const inputRef = useRef<HTMLInputElement>(null)

    const setRef = (element: HTMLInputElement | null) => {
      if (typeof ref === 'function') {
        ref(element)
      } else if (ref && 'current' in ref) {
        ref.current = element
      }

      inputRef.current = element
    }

    const getCurrentElement = () => {
      if (typeof ref === 'function') {
        return inputRef.current
      } else if (ref && 'current' in ref) {
        return ref.current
      }
      return inputRef.current
    }

    useEffect(() => {
      setInputValue(value)
    }, [value])

    useEffect(() => {
      if (focus) {
        const element = getCurrentElement()
        if (element) {
          element.focus()
        }
      }
    }, [focus])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)
      onChangeHandler(name, newValue)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlurHandler) {
        onBlurHandler(name, e.target.value)
      }
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(e, name)
      }
    }

    const getWrapperClassName = () => {
      const classes = [cls.wrapper]
      if (error) classes.push(cls.error)
      if (disabled) classes.push(cls.disabled)
      if (className) classes.push(className)
      return classes.join(' ')
    }

    return (
      <div className={getWrapperClassName()}>
        {title && (
          <label htmlFor={name} className={cls.label}>
            {title}
            {required && <span className={cls.required}>*</span>}
          </label>
        )}
        <div className={cls.inputContainer}>
          <input
            ref={setRef}
            id={name}
            name={name}
            type={type}
            value={inputValue}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            className={cls.input}
            onKeyDown={onKeyDown}
            autoComplete={autoComplete}
          />
          {error && errorMessage && <div className={cls.errorMessage}>{errorMessage}</div>}
        </div>
      </div>
    )
  },
)

Input.displayName = 'Input'

export default Input
